import { useState } from 'react';

export default function EmergencyContact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    urgency: '',
    service: '',
    address: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const formResponse = await fetch('https://readdy.ai/api/form/maintenance-urgence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-form-urlencoded' },
        body: formBody.toString()
      });

      try {
        await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-contact-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            projectType: formData.urgency,
            message: formData.description,
            formType: 'maintenance'
          })
        });
      } catch (emailError) {
        console.log('Email notification failed, but form was submitted successfully');
      }

      if (formResponse.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', urgency: '', service: '', address: '', description: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 bg-gray-950">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-base font-semibold leading-7 text-orange-400">Contact d'Urgence</h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Besoin d'une Intervention Rapide ?
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-400 mb-8">
              Notre équipe de techniciens qualifiés est disponible 24h/24 et 7j/7 pour tous vos besoins en maintenance et dépannage CVSE. Intervention rapide dans toute la Suisse romande.
            </p>
            <div className="space-y-6 mb-8">
              {[
                { icon: 'ri-phone-line', title: 'Téléphone', value: 'Filipe: +41 76 480 31 88 | Francisco: +41 76 565 42 03', sub: 'Urgences 24h/7j' },
                { icon: 'ri-mail-line', title: 'Email', value: 'info@casarenovaplus.ch', sub: 'Pour demandes non urgentes' },
                { icon: 'ri-map-pin-line', title: 'Zone d\'Intervention', value: 'Neuenegg et Suisse Romande', sub: 'Siège: Austrasse 95, 3176 Neuenegg' }
              ].map((item, i) => (
                <div key={i} className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-lg mr-4 flex-shrink-0">
                    <i className={`${item.icon} text-2xl text-orange-400`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-gray-300">{item.value}</p>
                    <p className="text-sm text-gray-500 mt-1">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-orange-500/10 rounded-xl p-6 border border-orange-500/20">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-2xl text-orange-400 flex-shrink-0 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-white mb-2">Contrats de Maintenance</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    Bénéficiez d'interventions prioritaires et de tarifs préférentiels avec nos contrats de maintenance préventive. Visites régulières programmées pour éviter les pannes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
            <h3 className="text-2xl font-bold text-white mb-6">Demande d'Intervention</h3>
            <form id="maintenance-urgence-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">Nom complet *</label>
                  <input
                    type="text" id="name" name="name" required
                    value={formData.name} onChange={handleChange}
                    className="w-full px-4 py-3 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                    <input
                      type="email" id="email" name="email" required
                      value={formData.email} onChange={handleChange}
                      className="w-full px-4 py-3 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                      placeholder="jean@exemple.ch"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-300 mb-2">Téléphone *</label>
                    <input
                      type="tel" id="phone" name="phone" required
                      value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                      placeholder="+41 XX XXX XX XX"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="urgency" className="block text-sm font-semibold text-gray-300 mb-2">Niveau d'urgence *</label>
                    <select
                      id="urgency" name="urgency" required
                      value={formData.urgency} onChange={handleChange}
                      className="w-full px-4 py-3 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all cursor-pointer"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="Urgence immédiate">Urgence immédiate</option>
                      <option value="Urgent (24h)">Urgent (24h)</option>
                      <option value="Normal (48h)">Normal (48h)</option>
                      <option value="Planifié">Planifié</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="service" className="block text-sm font-semibold text-gray-300 mb-2">Type de service *</label>
                    <select
                      id="service" name="service" required
                      value={formData.service} onChange={handleChange}
                      className="w-full px-4 py-3 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all cursor-pointer"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="Chauffage">Chauffage</option>
                      <option value="Électricité">Électricité</option>
                      <option value="Plomberie">Plomberie</option>
                      <option value="Ventilation">Ventilation</option>
                      <option value="Climatisation">Climatisation</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-300 mb-2">Adresse d'intervention *</label>
                  <input
                    type="text" id="address" name="address" required
                    value={formData.address} onChange={handleChange}
                    className="w-full px-4 py-3 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                    placeholder="Rue, numéro, code postal, ville"
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-gray-300 mb-2">Description du problème *</label>
                  <textarea
                    id="description" name="description" required rows={4} maxLength={500}
                    value={formData.description} onChange={handleChange}
                    className="w-full px-4 py-3 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Décrivez le problème rencontré..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 caractères</p>
                </div>
                <button
                  type="submit" disabled={isSubmitting}
                  className="w-full bg-orange-500 text-white px-6 py-4 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                </button>
                {submitStatus === 'success' && (
                  <div className="bg-green-900/30 border border-green-700 text-green-400 px-4 py-3 rounded-lg">
                    <p className="font-semibold">Demande reçue !</p>
                    <p className="text-sm mt-1">Nous vous contacterons rapidement pour planifier l'intervention.</p>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-3 rounded-lg">
                    <p className="font-semibold">Une erreur est survenue</p>
                    <p className="text-sm mt-1">Veuillez nous contacter par email : info@casarenovaplus.ch</p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
